/**
 * useAuthEnabled.ts — Shared auth-gate hook for protected TanStack Query hooks.
 *
 * WHY:
 *   All protected API routes require authentication. Without an `enabled` gate,
 *   react-query fires queries immediately on component mount — before Supabase has
 *   resolved the initial session from localStorage. At that point the Bearer token
 *   is still unknown, so every request returns COOKIE_MISSING 401 and stays in
 *   error state for the page's lifetime (re-auth event only invalidates /auth/me).
 *
 * HOW:
 *   Returns true only after the initial Supabase session check has completed AND
 *   there is a valid session. Queries gated on this value are kept in "pending"
 *   state (not yet fetched, not errored) during the loading window, then fire
 *   automatically the moment auth resolves — with a valid Bearer token already
 *   registered in the fetch pipeline (bearerFetchPatch.ts).
 *
 * DEV BYPASS:
 *   In local development with VITE_ENFORCE_SECURITY unset, there is no Supabase
 *   session (dev seed user uses cookie auth only). The bypass allows queries to
 *   fire in dev without a real JWT, matching the existing Shell.tsx dev-bypass.
 */

import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';

// Mirror the same bypass logic used in Shell.tsx so behavior is consistent.
const DEV_AUTH_BYPASS =
  import.meta.env.DEV === true &&
  import.meta.env.VITE_ENFORCE_SECURITY !== 'true';

/**
 * Returns true when it is safe to fire a protected API query.
 *
 * Use as the `enabled` option in any useQuery hook that calls a protected endpoint:
 *
 *   const authEnabled = useAuthEnabled();
 *   return useQuery({ ..., enabled: authEnabled });
 */
export function useAuthEnabled(): boolean {
  const { isAuthReady, session } = useSupabaseAuth();
  return DEV_AUTH_BYPASS || (isAuthReady && session !== null);
}
